import type { NextApiRequest, NextApiResponse } from 'next';
import { withAuth } from '@/lib/apiAuth';
import { supabaseAdmin } from '@/lib/supabaseAdmin';

// =============================================
// Duplicate Invoice: Creates a new borrador copy
// =============================================

async function generateNextNumber(serieId: string): Promise<{ numero: string; nextSeq: number }> {
  const { data: serie, error } = await supabaseAdmin
    .from('invoice_series')
    .select('*')
    .eq('id', serieId)
    .single();

  if (error || !serie) {
    throw new Error('Serie no encontrada');
  }

  if (!serie.activa) {
    throw new Error('La serie no está activa');
  }

  const seq = serie.siguiente_numero ?? 1;
  const prefijo = serie.prefijo ?? '';
  const paddedNum = String(seq).padStart(5, '0');
  const numero = `${prefijo}${paddedNum}`;

  return { numero, nextSeq: seq + 1 };
}

export default withAuth(async (req, res, user) => {
  if (req.method !== 'POST') {
    return res.status(405).json({ error: 'Método no permitido' });
  }

  try {
    const { invoice_id } = req.body;

    if (!invoice_id) {
      return res.status(400).json({ error: 'invoice_id es requerido' });
    }

    // Fetch original invoice
    const { data: original, error: fetchError } = await supabaseAdmin
      .from('invoices')
      .select('*')
      .eq('id', invoice_id)
      .single();

    if (fetchError || !original) {
      return res.status(404).json({ error: 'Factura original no encontrada' });
    }

    // Fetch original lines
    const { data: originalLines } = await supabaseAdmin
      .from('invoice_lines')
      .select('*')
      .eq('invoice_id', invoice_id)
      .order('orden', { ascending: true });

    // Generate new number from same series
    let numero: string;
    let nextSeq: number;
    try {
      const result = await generateNextNumber(original.serie_id);
      numero = result.numero;
      nextSeq = result.nextSeq;
    } catch (err: any) {
      return res.status(400).json({ error: err.message });
    }

    // Create new invoice (copy all fields except identity, state, dates)
    const newInvoice = {
      empresa_id: original.empresa_id,
      serie_id: original.serie_id,
      numero,
      tipo: original.tipo,
      estado: 'borrador',
      cliente_nombre: original.cliente_nombre,
      cliente_nif: original.cliente_nif,
      cliente_direccion: original.cliente_direccion,
      cliente_email: original.cliente_email,
      cliente_telefono: original.cliente_telefono,
      fecha_emision: new Date().toISOString().split('T')[0],
      fecha_vencimiento: null,
      fecha_operacion: null,
      moneda: original.moneda,
      tipo_cambio: original.tipo_cambio,
      base_imponible: original.base_imponible,
      total_impuestos: original.total_impuestos,
      total_retenciones: original.total_retenciones,
      total_suplidos: original.total_suplidos,
      total_factura: original.total_factura,
      tipo_operacion: original.tipo_operacion,
      clave_regimen: original.clave_regimen,
      factura_rectificada_id: null,
      motivo_rectificacion: null,
      es_recurrente: false,
      recurrente_config: null,
      generada_desde: null,
      verifactu_enviada: false,
      verifactu_id: null,
      verifactu_qr: null,
      verifactu_fecha_envio: null,
      verifactu_estado: null,
      face_enviada: false,
      face_numero_registro: null,
      face_estado: null,
      email_enviado: false,
      email_fecha_envio: null,
      pdf_url: null,
      pdf_generado: false,
      notas: original.notas,
      notas_internas: original.notas_internas,
      created_by: user.id,
    };

    const { data: createdInvoice, error: insertError } = await supabaseAdmin
      .from('invoices')
      .insert(newInvoice)
      .select()
      .single();

    if (insertError) {
      console.error('[API duplicate] Insert error:', insertError);
      return res.status(500).json({ error: `Error al duplicar factura: ${insertError.message}` });
    }

    // Copy lines
    if (originalLines && originalLines.length > 0) {
      const newLines = originalLines.map((line: Record<string, unknown>) => ({
        invoice_id: createdInvoice.id,
        orden: line.orden,
        concepto: line.concepto,
        descripcion: line.descripcion,
        cantidad: line.cantidad,
        precio_unitario: line.precio_unitario,
        descuento_porcentaje: line.descuento_porcentaje,
        base_linea: line.base_linea,
        tipo_iva: line.tipo_iva,
        porcentaje_iva: line.porcentaje_iva,
        cuota_iva: line.cuota_iva,
        recargo_porcentaje: line.recargo_porcentaje,
        recargo_cuota: line.recargo_cuota,
        retencion_porcentaje: line.retencion_porcentaje,
        retencion_cuota: line.retencion_cuota,
        es_suplido: line.es_suplido,
        suplido_nif_tercero: line.suplido_nif_tercero,
      }));

      const { error: linesError } = await supabaseAdmin.from('invoice_lines').insert(newLines);

      if (linesError) {
        console.error('[API duplicate] Lines insert error:', linesError);
        // Rollback the invoice
        await supabaseAdmin.from('invoices').delete().eq('id', createdInvoice.id);
        return res.status(500).json({ error: `Error al copiar líneas: ${linesError.message}` });
      }
    }

    // Update series siguiente_numero
    await supabaseAdmin
      .from('invoice_series')
      .update({ siguiente_numero: nextSeq })
      .eq('id', original.serie_id);

    // Return full invoice with lines
    const { data: lines } = await supabaseAdmin
      .from('invoice_lines')
      .select('*')
      .eq('invoice_id', createdInvoice.id)
      .order('orden', { ascending: true });

    return res.status(201).json({
      success: true,
      data: { ...createdInvoice, lines: lines ?? [] },
      duplicatedFrom: invoice_id,
    });
  } catch (error: any) {
    console.error('[API duplicate] Unexpected error:', error);
    return res.status(500).json({ error: error.message || 'Error interno del servidor' });
  }
});
